import type {
  ArithOp,
  CompareOp,
  Diagnostic,
  Expr,
  PrintEnd,
  Program,
  ScalarType,
  Stmt,
} from '../ir.js';
import { stmtExprs, walkExpr, walkStmts } from '../ir.js';
import { inferTypes } from '../types.js';
import type { LineRange } from '../writer.js';
import { LexError } from './lex.js';
import { lexPy, type PyToken } from './pylex.js';
import { sliceLength } from './slice.js';
import type { Span, TextError, TextProgram } from './source.js';

const COMPARE: Record<string, CompareOp> = {
  '==': '==',
  '!=': '!=',
  '<': '<',
  '<=': '<=',
  '>': '>',
  '>=': '>=',
};

const COMPARE_OPS = Object.keys(COMPARE);

/** Real Python we deliberately do not support yet; naming them makes the error useful. */
const UNSUPPORTED_WORDS = new Set([
  'def',
  'class',
  'import',
  'from',
  'lambda',
  'return',
  'try',
  'except',
  'finally',
  'raise',
  'with',
  'global',
  'nonlocal',
  'yield',
  'assert',
  'del',
  'continue',
  'async',
  'await',
  'is',
  'None',
]);

/** Calls that exist in Python but have no block behind them. */
const UNSUPPORTED_CALLS = new Set([
  'abs',
  'min',
  'max',
  'sum',
  'round',
  'pow',
  'sorted',
  'list',
  'set',
  'dict',
  'tuple',
  'open',
  'map',
  'filter',
  'enumerate',
  'zip',
  'range',
  'bool',
]);

class Fail extends Error {
  constructor(readonly info: TextError) {
    super(info.code);
  }
}

/**
 * `input()` is a step, not a value: the blocks only offer it as the whole value of a "set" or of a
 * "put in place", and the interpreter can only run it there. `print(input())` is valid Python that
 * this program cannot run, so it gets the same diagnostic the blocks give (parse.ts `allowRead`)
 * rather than dying half-way through the run.
 */
function readPlacement(body: readonly Stmt[]): Diagnostic[] {
  const out: Diagnostic[] = [];
  for (const s of walkStmts(body)) {
    for (const e of stmtExprs(s)) {
      const whole = (s.k === 'set' || s.k === 'setAt') && e === s.value;
      for (const x of walkExpr(e)) {
        if (x.k === 'read' && !(whole && x === e)) {
          out.push({ code: 'read-placement', blockId: x.id });
        }
      }
    }
  }
  return out;
}

/**
 * The Python a PebbleCode student writes: everything the Python view generates, plus the ordinary
 * ways the same program is written by hand (any spacing, comments, tabs or spaces, parentheses
 * around conditions, `x += 1`). Anything else is reported with a line and a reason.
 *
 * Python declares nothing, so the read kind comes from the call — `int(input())`, `float(input())`,
 * `input()` — and the shared `inferTypes` does the rest. Division needs no guessing either: `//` is
 * whole-number division and `/` is real division, whatever the operands are.
 */
class PyParser {
  private toks: PyToken[];
  private i = 0;
  /** Last token that is part of the program text (not a newline/indent/dedent), for end spans. */
  private last: PyToken;
  private readonly lineMap: Record<string, LineRange> = {};
  private readonly headMap: Record<string, LineRange> = {};
  private readonly spans: Record<string, Span> = {};
  /** Variables whose first value is text: `a + b` on one of them joins instead of adding. */
  private readonly textVars = new Set<string>();
  /** Ids of values written `str(x)`: text, whatever `x` is. */
  private readonly strCast = new Set<string>();
  /** Variables that hold a list (rank 1) or a table (rank 2), so `len(a)` is its size. */
  private readonly arrayRank = new Map<string, 1 | 2>();
  /** What one place of such a variable holds, so `a[i] + b` joins when the places are text. */
  private readonly arrayElem = new Map<string, ScalarType>();
  private exprCount = 0;

  constructor(toks: PyToken[]) {
    this.toks = toks;
    this.last = toks[0]!;
  }

  // ---------- token helpers ----------

  private get tok(): PyToken {
    return this.toks[this.i] ?? this.toks[this.toks.length - 1]!;
  }

  /** A method, not a comparison on `tok`, so narrowing never leaks between loops. */
  private atEnd(): boolean {
    return this.toks[this.i]?.kind === 'eof';
  }

  private at(text: string): boolean {
    const t = this.tok;
    return t.text === text && t.kind !== 'string' && t.kind !== 'fstring';
  }

  private atKind(kind: PyToken['kind']): boolean {
    return this.tok.kind === kind;
  }

  private next(): PyToken {
    const t = this.tok;
    if (this.i < this.toks.length - 1) this.i++;
    if (t.kind !== 'newline' && t.kind !== 'indent' && t.kind !== 'dedent' && t.kind !== 'eof') {
      this.last = t;
    }
    return t;
  }

  private eat(text: string): boolean {
    if (!this.at(text)) return false;
    this.next();
    return true;
  }

  private expect(text: string): PyToken {
    if (!this.at(text)) this.fail('expected', { expected: text, found: this.shown() });
    return this.next();
  }

  /** How the token the parser is looking at should be named in a message. */
  private shown(): string {
    switch (this.tok.kind) {
      case 'newline':
        return 'end of line';
      case 'indent':
        return 'an indented line';
      case 'dedent':
      case 'eof':
        return 'end';
      case 'string':
        return 'text';
      case 'fstring':
        return 'text';
      default:
        return this.tok.text;
    }
  }

  private fail(code: TextError['code'], params?: Record<string, string>): never {
    const t = this.tok;
    throw new Fail({
      code,
      line: t.line,
      column: t.column,
      span: { from: t.from, to: Math.max(t.to, t.from + 1) },
      ...(params ? { params } : {}),
    });
  }

  // ---------- ids and maps ----------

  /** Ids are a pure function of the parse tree, so the server re-parsing the text mints the same. */
  private stmtId(path: string): string {
    return `t:${path}`;
  }

  private childId(path: string, index: number, tag = ''): string {
    return path === '' ? `${index}${tag}` : `${path}.${index}${tag}`;
  }

  private exprId(stmtId: string): string {
    return `${stmtId}/e${++this.exprCount}`;
  }

  private mark(id: string, start: PyToken, endTok: PyToken, headEnd = endTok): void {
    this.lineMap[id] = [start.line, endTok.line];
    this.headMap[id] = [start.line, headEnd.line];
    this.spans[id] = { from: start.from, to: endTok.to };
  }

  private markExpr(e: Expr, start: PyToken, end: PyToken): void {
    this.lineMap[e.id] ??= [start.line, end.line];
    this.spans[e.id] ??= { from: start.from, to: end.to };
  }

  // ---------- statements ----------

  parse(): Stmt[] {
    const body = this.statements('');
    if (!this.atEnd()) this.fail('expected', { expected: 'end', found: this.shown() });
    return body;
  }

  private statements(path: string): Stmt[] {
    const out: Stmt[] = [];
    while (!this.atEnd() && !this.atKind('dedent')) {
      this.statement(path, out);
    }
    return out;
  }

  /** One written line; `a = 1; b = 2` puts two statements in `out`. */
  private statement(path: string, out: Stmt[]): void {
    const start = this.tok;
    const id = this.stmtId(this.childId(path, out.length));

    if (start.kind === 'ident' && UNSUPPORTED_WORDS.has(start.text)) {
      this.fail('unsupported', { what: start.text });
    }
    // An indented line where nothing opened a block: the student moved one line too far right.
    if (this.atKind('indent')) this.fail('expected', { expected: 'a line that is not indented' });
    if (this.at('if')) return this.ifStatement(id, path, out, start);
    if (this.at('while')) return this.whileStatement(id, path, out, start);
    if (this.at('for')) return this.forStatement(id, path, out, start);
    if (this.atAnyWord('elif', 'else'))
      this.fail('expected', { expected: 'if', found: start.text });

    this.simple(path, out);
    while (this.eat(';')) {
      if (this.atKind('newline') || this.atEnd()) break;
      this.simple(path, out);
    }
    this.endOfLine();
  }

  private atAnyWord(...words: string[]): boolean {
    return this.tok.kind === 'ident' && words.includes(this.tok.text);
  }

  /** A statement that fits on one line; leaves the line end for the caller. */
  private simple(path: string, out: Stmt[]): void {
    const start = this.tok;
    const id = this.stmtId(this.childId(path, out.length));
    if (this.at('pass')) {
      // An empty body; the generator writes it, and it adds nothing to the program.
      this.next();
      return;
    }
    if (this.at('break')) {
      this.next();
      out.push({ k: 'break', id });
      this.mark(id, start, this.last);
      return;
    }
    if (this.atAnyWord('print') && this.toks[this.i + 1]?.text === '(') {
      return this.printStatement(id, out, start);
    }
    return this.assignment(id, out, start);
  }

  private endOfLine(): void {
    if (this.atKind('newline')) {
      this.next();
      return;
    }
    if (this.atEnd() || this.atKind('dedent')) return;
    this.fail('expected', { expected: 'end of line', found: this.shown() });
  }

  private assignment(id: string, out: Stmt[], start: PyToken): void {
    if (this.tok.kind !== 'ident') {
      this.fail('expected', { expected: 'a name', found: this.shown() });
    }
    const name = this.next().text;
    // A call or a method where a plain name should be: neither is a block.
    if (this.at('(')) this.fail('unsupported', { what: `${name}()` });
    if (this.at('.')) this.fail('unsupported', { what: `${name}.…` });
    if (this.at('[')) return this.setAtStatement(id, out, start, name);
    if (this.atAnyOp('+=', '-=', '*=', '/=', '//=', '%=')) {
      const op = this.next().text;
      const value = this.expression(id);
      out.push(this.compound(id, name, op, value));
      this.mark(id, start, this.last);
      return;
    }
    this.expect('=');
    if (this.at('[')) {
      // `a = [0] * (n)` and friends make a list or a table, not a value.
      const { elem, dims } = this.arrayLiteral(id);
      this.noteArray(name, dims.length === 2 ? 2 : 1, elem);
      out.push({ k: 'newArray', id, name, elem, dims, base: 0 });
      this.mark(id, start, this.last);
      return;
    }
    const value = this.expression(id);
    if (this.at('=')) this.fail('unsupported', { what: 'a = b = c' });
    this.noteType(name, value);
    out.push({ k: 'set', id, name, value });
    this.mark(id, start, this.last);
  }

  /** `a[i] = v`, `a[i][j] = v`, and the shorthands `a[i] += 1` and friends. */
  private setAtStatement(id: string, out: Stmt[], start: PyToken, name: string): void {
    if (this.textVars.has(name)) this.fail('unsupported', { what: `${name}[…] on text` });
    const [index, index2] = this.indexes(id);
    this.noteArray(name, index2 ? 2 : 1);
    let value: Expr;
    if (this.atAnyOp('+=', '-=', '*=', '/=', '//=', '%=')) {
      const op = this.next().text;
      const right = this.expression(id);
      // The place is read as well as written, and the two readings are separate values.
      const target: Expr = {
        k: 'at',
        id: this.exprId(id),
        name,
        index: this.cloneExpr(id, index),
        index2: index2 ? this.cloneExpr(id, index2) : null,
      };
      value = this.shorthand(id, target, op, right);
    } else {
      this.expect('=');
      value = this.expression(id);
    }
    out.push({ k: 'setAt', id, name, index, index2, value });
    this.mark(id, start, this.last);
  }

  /** `[i]`, or `[i][j]` for a table. */
  private indexes(id: string): [Expr, Expr | null] {
    this.expect('[');
    const index = this.expression(id);
    this.expect(']');
    if (!this.at('[')) return [index, null];
    this.next();
    const index2 = this.expression(id);
    this.expect(']');
    return [index, index2];
  }

  /** What `target op= right` puts back in the place: `a[i] += 1` is `a[i] = a[i] + 1`. */
  private shorthand(id: string, target: Expr, op: string, right: Expr): Expr {
    const arith: ArithOp =
      op === '+='
        ? '+'
        : op === '-='
          ? '-'
          : op === '*='
            ? '*'
            : op === '/='
              ? '/'
              : op === '//='
                ? 'idiv'
                : 'mod';
    if (arith === '+' && (this.isText(target) || this.isText(right))) {
      return this.join(id, target, right);
    }
    return { k: 'arith', id: this.exprId(id), op: arith, left: target, right };
  }

  /**
   * A list display: `[0] * (n)`, `[0 for _ in range(n)]`, `[[0] * (m) for _ in range(n)]` or `[]`.
   * Only a starting value the blocks can make (`0`, `0.0`, `''`, `False`) is allowed: there is no
   * IR for a list written out with real values in it.
   */
  private arrayLiteral(id: string): { elem: ScalarType; dims: [Expr] | [Expr, Expr] } {
    this.expect('[');
    if (this.eat(']')) {
      // `a = []` — a list the student will fill; it starts with no places at all.
      return { elem: 'int', dims: [this.num(id, 0)] };
    }
    if (this.at('[')) {
      const row = this.arrayLiteral(id);
      if (row.dims.length !== 1) this.fail('unsupported', { what: 'a list of tables' });
      // `[[0] * m] * n` makes n names for ONE row in Python: writing to one writes to all.
      if (this.at(']')) this.fail('unsupported', { what: 'a table made with *' });
      return { elem: row.elem, dims: [this.listSize(id), row.dims[0]] };
    }
    const elem = this.zeroLiteral();
    return { elem, dims: [this.listSize(id)] };
  }

  /** How many places: the `] * n` or the `for _ in range(n)]` after the first element. */
  private listSize(id: string): Expr {
    if (this.eat(']')) {
      if (!this.atAnyOp('*')) this.fail('unsupported', { what: 'a list with no size' });
      this.next();
      return this.expression(id);
    }
    if (!this.atAnyWord('for')) this.fail('expected', { expected: ']', found: this.shown() });
    this.next();
    // The counter of the comprehension is never used; any name will do.
    if (this.tok.kind !== 'ident')
      this.fail('expected', { expected: 'a name', found: this.shown() });
    this.next();
    if (!this.at('in')) this.fail('expected', { expected: 'in', found: this.shown() });
    this.next();
    if (!this.atAnyWord('range') || this.toks[this.i + 1]?.text !== '(') {
      this.fail('unsupported', { what: 'a list made from anything but range()' });
    }
    this.next();
    this.expect('(');
    const size = this.expression(id);
    if (this.at(',')) this.fail('unsupported', { what: 'range() with two values in a list' });
    this.expect(')');
    this.expect(']');
    return size;
  }

  /** The value every place starts at, which is what says whether it is a list of numbers or text. */
  private zeroLiteral(): ScalarType {
    const t = this.tok;
    if (t.kind === 'number' && Number(t.text) === 0) {
      this.next();
      return t.text.includes('.') ? 'double' : 'int';
    }
    if (t.kind === 'string' && t.text === '') {
      this.next();
      return 'string';
    }
    if (t.kind === 'ident' && t.text === 'False') {
      this.next();
      return 'bool';
    }
    this.fail('unsupported', { what: 'a list written out with values in it' });
  }

  /** Remembers that a name holds a list or a table, which is what makes `len(a)` its size. */
  private noteArray(name: string, rank: 1 | 2, elem?: ScalarType): void {
    this.arrayRank.set(name, rank);
    if (elem !== undefined) this.arrayElem.set(name, elem);
    this.textVars.delete(name);
  }

  /** A copy with fresh ids, so a place read and written on one line is two values, not one. */
  private cloneExpr(sid: string, e: Expr): Expr {
    const id = this.exprId(sid);
    switch (e.k) {
      case 'arith':
      case 'compare':
      case 'logic':
      case 'join':
        return { ...e, id, left: this.cloneExpr(sid, e.left), right: this.cloneExpr(sid, e.right) };
      case 'not':
      case 'length':
      case 'code':
      case 'chr':
      case 'case':
        return { ...e, id, arg: this.cloneExpr(sid, e.arg) };
      case 'find':
        return {
          ...e,
          id,
          needle: this.cloneExpr(sid, e.needle),
          hay: this.cloneExpr(sid, e.hay),
        };
      case 'substr':
        return {
          ...e,
          id,
          arg: this.cloneExpr(sid, e.arg),
          from: this.cloneExpr(sid, e.from),
          len: this.cloneExpr(sid, e.len),
        };
      case 'at':
        return {
          ...e,
          id,
          index: this.cloneExpr(sid, e.index),
          index2: e.index2 ? this.cloneExpr(sid, e.index2) : null,
        };
      default:
        return { ...e, id };
    }
  }

  private atAnyOp(...texts: string[]): boolean {
    return this.tok.kind === 'op' && texts.includes(this.tok.text);
  }

  /** `x += 1` counts up; `x *= 2` is just `x = x * 2`. */
  private compound(id: string, name: string, op: string, value: Expr): Stmt {
    if (op === '+=' && (this.textVars.has(name) || this.isText(value))) {
      this.textVars.add(name);
      return { k: 'set', id, name, value: this.join(id, this.var(id, name), value) };
    }
    if (op === '+=') return { k: 'change', id, name, delta: value };
    if (op === '-=') {
      // `x -= 1` is one "change by -1"; anything else counts down by the value.
      const delta: Expr =
        value.k === 'num'
          ? { ...value, value: -value.value }
          : { k: 'arith', id: this.exprId(id), op: '-', left: this.num(id, 0), right: value };
      return { k: 'change', id, name, delta };
    }
    const arith: ArithOp = op === '*=' ? '*' : op === '/=' ? '/' : op === '//=' ? 'idiv' : 'mod';
    return {
      k: 'set',
      id,
      name,
      value: {
        k: 'arith',
        id: this.exprId(id),
        op: arith,
        left: this.var(id, name),
        right: value,
      },
    };
  }

  /** Remembers the variables that hold text, so `+` on them joins (Python has no declarations). */
  private noteType(name: string, value: Expr): void {
    if (this.isText(value)) this.textVars.add(name);
  }

  private printStatement(id: string, out: Stmt[], start: PyToken): void {
    this.next();
    this.expect('(');
    const parts: Expr[] = [];
    let end: PrintEnd = 'newline';
    while (!this.at(')')) {
      if (this.atAnyWord('end', 'sep') && this.toks[this.i + 1]?.text === '=') {
        const word = this.next().text;
        this.next();
        const t = this.tok;
        if (word === 'sep' || t.kind !== 'string') {
          this.fail('unsupported', { what: `${word}=` });
        }
        this.next();
        if (t.text === '\n') end = 'newline';
        else if (t.text === ' ') end = 'space';
        else if (t.text === '') end = 'none';
        else this.fail('unsupported', { what: 'end=' });
      } else {
        const from = this.tok;
        const e = this.expression(id);
        this.markExpr(e, from, this.last);
        parts.push(e);
      }
      if (!this.eat(',')) break;
    }
    this.expect(')');
    // `print(a, b)` writes a space between the values, which is a join with " " in the middle.
    const value =
      parts.length === 0
        ? this.text(id, '')
        : parts.reduce((a, b) => this.join(id, this.join(id, a, this.text(id, ' ')), b));
    out.push({ k: 'print', id, value, end });
    this.mark(id, start, this.last);
  }

  private ifStatement(id: string, path: string, out: Stmt[], start: PyToken): void {
    this.next();
    const cond = this.expression(id);
    const branches: { cond: Expr; body: Stmt[] }[] = [];
    const first = this.block(this.childId(path, out.length, 'b0'));
    branches.push({ cond, body: first.body });
    let orElse: Stmt[] | null = null;
    while (this.atAnyWord('elif', 'else')) {
      if (this.at('elif')) {
        this.next();
        const c = this.expression(id);
        const b = this.block(this.childId(path, out.length, `b${branches.length}`));
        branches.push({ cond: c, body: b.body });
        continue;
      }
      this.next();
      orElse = this.block(this.childId(path, out.length, 'e')).body;
      break;
    }
    out.push({ k: 'if', id, branches, orElse });
    this.mark(id, start, this.last, first.colon);
  }

  private whileStatement(id: string, path: string, out: Stmt[], start: PyToken): void {
    this.next();
    const cond = this.expression(id);
    const body = this.block(this.childId(path, out.length));
    out.push({ k: 'while', id, cond, body: body.body });
    this.mark(id, start, this.last, body.colon);
  }

  /** `for i in range(a, b)`, `range(a, b, -1)`, `range(n)` — and `for _ in …`, which is "repeat". */
  private forStatement(id: string, path: string, out: Stmt[], start: PyToken): void {
    this.next();
    if (this.tok.kind !== 'ident')
      this.fail('expected', { expected: 'a name', found: this.shown() });
    const name = this.next().text;
    if (this.at(',')) this.fail('unsupported', { what: 'for a, b in …' });
    if (!this.at('in')) this.fail('expected', { expected: 'in', found: this.shown() });
    this.next();
    if (!this.atAnyWord('range') || this.toks[this.i + 1]?.text !== '(') {
      this.fail('unsupported', { what: 'a for loop over anything but range()' });
    }
    this.next();
    this.expect('(');
    const args: Expr[] = [this.expression(id)];
    while (this.eat(',')) args.push(this.expression(id));
    this.expect(')');
    if (args.length > 3) this.fail('unsupported', { what: 'range() with four values' });

    let down = false;
    if (args.length === 3) {
      const step = args[2]!;
      if (step.k !== 'num' || (step.value !== 1 && step.value !== -1)) {
        this.fail('unsupported', { what: 'a range() step other than 1 or -1' });
      }
      down = step.value === -1;
    }
    const from = args.length === 1 ? this.num(id, 0) : args[0]!;
    const stop = args.length === 1 ? args[0]! : args[1]!;
    // range() stops *before* its second value; the block loop counts up to `to` inclusive.
    const to = this.offsetBack(id, stop, down ? 1 : -1);
    const body = this.block(this.childId(path, out.length));

    if (name === '_' && args.length === 1) {
      // `for _ in range(n)` is exactly the "repeat n times" block: no counter to speak of.
      out.push({ k: 'repeat', id, times: args[0]!, body: body.body });
    } else {
      out.push({ k: 'for', id, name, from, to, down, body: body.body });
    }
    this.mark(id, start, this.last, body.colon);
  }

  /**
   * Undoes the `+ 1` / `- 1` the generator writes around a range end, folding it back into the
   * expression the same way `offset()` in gen/py.ts folded it out, so the tree round-trips.
   */
  private offsetBack(id: string, e: Expr, by: number): Expr {
    if (e.k === 'num' && Number.isInteger(e.value)) return { ...e, value: e.value + by };
    if (e.k === 'arith' && (e.op === '+' || e.op === '-') && e.right.k === 'num') {
      const k = (e.op === '+' ? e.right.value : -e.right.value) + by;
      if (k === 0) return e.left;
      return {
        k: 'arith',
        id: e.id,
        op: k > 0 ? '+' : '-',
        left: e.left,
        right: { ...e.right, value: Math.abs(k) },
      };
    }
    return {
      k: 'arith',
      id: this.exprId(id),
      op: by > 0 ? '+' : '-',
      left: e,
      right: this.num(id, Math.abs(by)),
    };
  }

  /** `: <newline> <indented statements>`, or a single statement written after the colon. */
  private block(path: string): { body: Stmt[]; colon: PyToken } {
    const colon = this.expect(':');
    if (!this.atKind('newline')) {
      const body: Stmt[] = [];
      this.statement(path, body);
      return { body, colon };
    }
    this.next();
    if (!this.atKind('indent')) {
      this.fail('expected', { expected: 'an indented line', found: this.shown() });
    }
    this.next();
    const body = this.statements(path);
    if (this.atKind('dedent')) this.next();
    return { body, colon };
  }

  // ---------- expressions ----------

  private num(id: string, value: number): Expr {
    return { k: 'num', id: this.exprId(id), value };
  }

  private text(id: string, value: string): Expr {
    return { k: 'text', id: this.exprId(id), value };
  }

  private var(id: string, name: string): Expr {
    return { k: 'var', id: this.exprId(id), name };
  }

  private join(id: string, left: Expr, right: Expr): Expr {
    return { k: 'join', id: this.exprId(id), left, right };
  }

  /**
   * Whether a value is text. Python says so only at run time, so we go by what was written: a
   * literal, an f-string, `input()`, `str(x)`, or a variable whose first value was text. A `+`
   * with text on either side joins; everything else adds.
   */
  private isText(e: Expr): boolean {
    if (this.strCast.has(e.id)) return true;
    switch (e.k) {
      case 'text':
      case 'join':
        return true;
      case 'read':
        return e.as === 'text';
      case 'var':
        return this.textVars.has(e.name);
      case 'at':
        // A character of a text is text too; so is a place of a list of texts.
        return this.textVars.has(e.name) || this.arrayElem.get(e.name) === 'string';
      case 'chr':
      case 'case':
      case 'substr':
        return true;
      case 'arith':
        return e.op === '+' && (this.isText(e.left) || this.isText(e.right));
      default:
        return false;
    }
  }

  private expression(id: string): Expr {
    return this.orExpr(id);
  }

  private orExpr(id: string): Expr {
    const from = this.tok;
    let left = this.andExpr(id);
    while (this.atAnyWord('or')) {
      this.next();
      const right = this.andExpr(id);
      left = { k: 'logic', id: this.exprId(id), op: 'or', left, right };
      this.markExpr(left, from, this.last);
    }
    return left;
  }

  private andExpr(id: string): Expr {
    const from = this.tok;
    let left = this.notExpr(id);
    while (this.atAnyWord('and')) {
      this.next();
      const right = this.notExpr(id);
      left = { k: 'logic', id: this.exprId(id), op: 'and', left, right };
      this.markExpr(left, from, this.last);
    }
    return left;
  }

  private notExpr(id: string): Expr {
    if (this.atAnyWord('not')) {
      const from = this.next();
      const arg = this.notExpr(id);
      const e: Expr = { k: 'not', id: this.exprId(id), arg };
      this.markExpr(e, from, this.last);
      return e;
    }
    return this.compareExpr(id);
  }

  /** Non-associative, as in Python's grammar: `a < b < c` is a chain we do not run. */
  private compareExpr(id: string): Expr {
    const from = this.tok;
    const left = this.addExpr(id);
    if (!this.atAnyOp(...COMPARE_OPS)) return left;
    const op = this.next().text;
    const right = this.addExpr(id);
    const e: Expr = { k: 'compare', id: this.exprId(id), op: COMPARE[op]!, left, right };
    this.markExpr(e, from, this.last);
    if (this.atAnyOp(...COMPARE_OPS)) this.fail('unsupported', { what: 'a chained comparison' });
    return e;
  }

  private addExpr(id: string): Expr {
    const from = this.tok;
    let left = this.mulExpr(id);
    while (this.atAnyOp('+', '-')) {
      const op = this.next().text;
      const right = this.mulExpr(id);
      left =
        op === '+' && (this.isText(left) || this.isText(right))
          ? this.join(id, left, right)
          : { k: 'arith', id: this.exprId(id), op: op as ArithOp, left, right };
      this.markExpr(left, from, this.last);
    }
    return left;
  }

  private mulExpr(id: string): Expr {
    const from = this.tok;
    let left = this.unary(id);
    while (this.atAnyOp('*', '/', '//', '%')) {
      const op = this.next().text;
      const right = this.unary(id);
      // Python needs no guessing here: `//` is whole-number division and `/` always real.
      const arith: ArithOp = op === '//' ? 'idiv' : op === '%' ? 'mod' : (op as ArithOp);
      left = { k: 'arith', id: this.exprId(id), op: arith, left, right };
      this.markExpr(left, from, this.last);
    }
    return left;
  }

  private unary(id: string): Expr {
    if (this.atAnyOp('-')) {
      const from = this.next();
      const arg = this.unary(id);
      if (arg.k === 'num') return { ...arg, value: -arg.value };
      const e: Expr = {
        k: 'arith',
        id: this.exprId(id),
        op: '-',
        left: this.num(id, 0),
        right: arg,
      };
      this.markExpr(e, from, this.last);
      return e;
    }
    if (this.atAnyOp('+')) {
      this.next();
      return this.unary(id);
    }
    const e = this.methods(id, this.primary(id));
    if (this.atAnyOp('**')) this.fail('unsupported', { what: '**' });
    if (this.atAnyOp('[')) this.fail('unsupported', { what: '[]' });
    if (this.atAnyOp('.')) this.fail('unsupported', { what: '.' });
    return e;
  }

  /** The text methods a block stands behind: `c.upper()`, `c.lower()`, `s.find(t)`. */
  private methods(id: string, target: Expr): Expr {
    let e = target;
    while (this.atAnyOp('.') && this.toks[this.i + 1]?.kind === 'ident') {
      const from = this.next();
      const name = this.next();
      if (name.text !== 'upper' && name.text !== 'lower' && name.text !== 'find') {
        this.fail('unsupported', { what: `.${name.text}()` });
      }
      this.expect('(');
      if (name.text === 'find') {
        const needle = this.expression(id);
        const close = this.expect(')');
        e = { k: 'find', id: this.exprId(id), needle, hay: e };
        this.markExpr(e, from, close);
      } else {
        const close = this.expect(')');
        e = { k: 'case', id: this.exprId(id), upper: name.text === 'upper', arg: e };
        this.markExpr(e, from, close);
      }
    }
    return e;
  }

  private primary(id: string): Expr {
    const t = this.tok;
    if (t.kind === 'number') {
      this.next();
      const e = this.num(id, Number(t.text));
      this.markExpr(e, t, t);
      return e;
    }
    if (t.kind === 'string') {
      this.next();
      const e = this.text(id, t.text);
      this.markExpr(e, t, t);
      return e;
    }
    if (t.kind === 'fstring') {
      this.next();
      const e = this.fstring(id, t);
      this.markExpr(e, t, t);
      return e;
    }
    if (t.kind === 'ident') {
      if (t.text === 'True' || t.text === 'False') {
        this.next();
        const e: Expr = { k: 'bool', id: this.exprId(id), value: t.text === 'True' };
        this.markExpr(e, t, t);
        return e;
      }
      if (UNSUPPORTED_WORDS.has(t.text)) this.fail('unsupported', { what: t.text });
      this.next();
      if (this.at('(')) return this.call(id, t);
      if (this.at('[')) return this.atExpr(id, t);
      const e = this.var(id, t.text);
      this.markExpr(e, t, t);
      return e;
    }
    if (this.at('(')) {
      this.next();
      const inner = this.expression(id);
      this.expect(')');
      return inner;
    }
    this.fail('expected', { expected: 'a value', found: this.shown() });
  }

  /** One place of a list or a table: `a[i]` or `a[i][j]`; on a text, a character or a slice. */
  private atExpr(id: string, name: PyToken): Expr {
    if (this.textVars.has(name.text)) return this.textAt(id, name);
    const [index, index2] = this.indexes(id);
    // A table can be told from a list by the way it is written, if nothing has said so yet.
    if (!this.arrayRank.has(name.text)) this.arrayRank.set(name.text, index2 ? 2 : 1);
    const e: Expr = { k: 'at', id: this.exprId(id), name: name.text, index, index2 };
    this.markExpr(e, name, this.last);
    return e;
  }

  /** `s[i]` is one character; `s[a:b]`, `s[:b]` and `s[a:]` are a piece of the text. */
  private textAt(id: string, name: PyToken): Expr {
    const text = this.var(id, name.text);
    this.markExpr(text, name, name);
    this.expect('[');
    const from = this.at(':') ? this.num(id, 0) : this.expression(id);
    let e: Expr;
    if (this.eat(':')) {
      const len = this.at(']')
        ? // To the end: everything after `from`.
          ({
            k: 'arith',
            id: this.exprId(id),
            op: '-',
            left: { k: 'length', id: this.exprId(id), arg: this.var(id, name.text) },
            right: this.cloneExpr(id, from),
          } satisfies Expr)
        : sliceLength(from, this.expression(id), () => this.exprId(id));
      e = { k: 'substr', id: this.exprId(id), arg: text, from, len };
    } else {
      e = { k: 'at', id: this.exprId(id), name: name.text, index: from, index2: null };
    }
    this.expect(']');
    if (this.at('[')) this.fail('unsupported', { what: `${name.text}[…][…] on text` });
    this.markExpr(e, name, this.last);
    return e;
  }

  /** The handful of built-ins a block program can be written with. */
  private call(id: string, name: PyToken): Expr {
    if (UNSUPPORTED_CALLS.has(name.text)) this.fail('unsupported', { what: `${name.text}()` });
    this.expect('(');
    if (name.text === 'input') {
      if (!this.at(')')) this.fail('unsupported', { what: 'input() with a prompt' });
      const close = this.expect(')');
      const e: Expr = { k: 'read', id: this.exprId(id), as: 'text' };
      this.markExpr(e, name, close);
      return e;
    }
    const inner = this.expression(id);
    const close = this.expect(')');
    switch (name.text) {
      case 'int':
      case 'float': {
        const as = name.text === 'int' ? 'int' : 'double';
        // `int(input())` is one "read a whole number"; the cast is not a step of its own.
        if (inner.k === 'read') return { ...inner, as };
        // `int(a / b)` is how whole-number division is written before `//` is taught.
        if (as === 'int' && inner.k === 'arith' && inner.op === '/')
          return { ...inner, op: 'idiv' };
        return inner;
      }
      case 'str':
        // Nothing to do at run time; remember it so a `+` around it joins.
        this.strCast.add(inner.id);
        return inner;
      case 'len': {
        const e = this.lengthOrSize(id, inner);
        this.markExpr(e, name, close);
        return e;
      }
      case 'ord':
      case 'chr': {
        const e: Expr = {
          k: name.text === 'ord' ? 'code' : 'chr',
          id: this.exprId(id),
          arg: inner,
        };
        this.markExpr(e, name, close);
        return e;
      }
      default:
        this.fail('unsupported', { what: `${name.text}()` });
    }
  }

  /**
   * `len(a)` is two different blocks: how long a text is, and how many places a list has. A name
   * that was given a list wins the tie — that is what the student almost always means — and
   * `len(a[0])` on a table is how the generator writes the number of columns.
   */
  private lengthOrSize(id: string, inner: Expr): Expr {
    if (inner.k === 'var' && this.arrayRank.has(inner.name)) {
      return { k: 'size', id: this.exprId(id), name: inner.name, dim: 1 };
    }
    if (inner.k === 'at' && inner.index2 === null && this.arrayRank.get(inner.name) === 2) {
      if (inner.index.k !== 'num' || inner.index.value !== 0) {
        this.fail('unsupported', { what: 'len() of one row' });
      }
      return { k: 'size', id: this.exprId(id), name: inner.name, dim: 2 };
    }
    return { k: 'length', id: this.exprId(id), arg: inner };
  }

  /** `f"a{x}b"` is the join the text blocks produce, left to right. */
  private fstring(id: string, t: PyToken): Expr {
    const parts = (t.parts ?? []).map((p) =>
      p.kind === 'text' ? this.text(id, p.value) : this.hole(id, p.tokens),
    );
    if (parts.length === 0) return this.text(id, '');
    return parts.reduce((a, b) => this.join(id, a, b));
  }

  /** Parses one `{…}` of an f-string off its own tokens, keeping one expression-id counter. */
  private hole(id: string, tokens: PyToken[]): Expr {
    const savedToks = this.toks;
    const savedI = this.i;
    const savedLast = this.last;
    this.toks = tokens;
    this.i = 0;
    try {
      const e = this.expression(id);
      if (!this.atEnd()) this.fail('expected', { expected: '}', found: this.shown() });
      return e;
    } finally {
      this.toks = savedToks;
      this.i = savedI;
      this.last = savedLast;
    }
  }

  // ---------- after the parse ----------

  result(body: Stmt[]): {
    program: Program;
    lineMap: Record<string, LineRange>;
    headMap: Record<string, LineRange>;
    spans: Record<string, Span>;
  } {
    const program: Program = inferTypes({
      startId: 't:start',
      body,
      variables: [],
      types: {},
      diagnostics: readPlacement(body),
    });
    const lines = Object.values(this.lineMap);
    const last = lines.reduce((m, r) => Math.max(m, r[1]), 1);
    this.lineMap['t:start'] = [1, last];
    this.headMap['t:start'] = [1, 1];
    return { program, lineMap: this.lineMap, headMap: this.headMap, spans: this.spans };
  }
}

/** Parses Python text into the same Program IR the blocks produce. Never throws. */
export function parsePy(source: string): TextProgram {
  const empty = (errors: TextError[]): TextProgram => ({
    program: { startId: null, body: [], variables: [], types: {}, diagnostics: [] },
    code: { language: 'py', code: source, lineMap: {}, headMap: {} },
    spans: {},
    errors,
  });
  try {
    const parser = new PyParser(lexPy(source));
    const { program, lineMap, headMap, spans } = parser.result(parser.parse());
    return { program, code: { language: 'py', code: source, lineMap, headMap }, spans, errors: [] };
  } catch (e) {
    if (e instanceof Fail || e instanceof LexError) return empty([e.info]);
    throw e;
  }
}
